// @ts-nocheck
import React, {useContext} from "react";
import {ResponsiveLine} from "@nivo/line";
import ChartCard from "./ChartCard";
import StyleContext from "../../../contexts/StyleContext";
import {byRankGap} from "../data/fifaData";
import {
  FIFA_PALETTE,
  WIN_RATE_AXIS,
  coinFlipMarker,
  getNivoTheme
} from "./chartTheme";

// 4.4 Win rate by rank gap with 95% CIs (line + custom band). Centerpiece:
// signal only appears at large gaps. Nivo has no error bars, so the CI is a
// shaded polygon drawn behind the line; its width is the honesty signal.

// Buckets with fewer matches than this are flagged as low-confidence.
const SMALL_SAMPLE = 10;

// Lookup by bucket for the tooltip (Nivo's Point only carries x/y).
const byBucket = byRankGap.reduce((acc, d) => {
  acc[d.bucket] = d;
  return acc;
}, {});

const series = [
  {
    id: "winRate",
    data: byRankGap.map(d => ({
      x: d.bucket,
      y: d.winRate,
      ciLow: d.ciLow,
      ciHigh: d.ciHigh,
      matches: d.matches
    }))
  }
];

// Custom layer: shaded 95% CI band between ciLow and ciHigh at each point.
const ConfidenceBand = ({series: computedSeries, xScale, yScale}) => {
  const points = computedSeries[0] && computedSeries[0].data;
  if (!points || points.length === 0) {
    return null;
  }
  const topEdge = points
    .map(p => `${xScale(p.data.x)},${yScale(p.data.ciHigh)}`)
    .join(" ");
  const bottomEdge = points
    .slice()
    .reverse()
    .map(p => `${xScale(p.data.x)},${yScale(p.data.ciLow)}`)
    .join(" ");
  return (
    <polygon
      points={`${topEdge} ${bottomEdge}`}
      fill={FIFA_PALETTE.band}
      stroke="none"
    />
  );
};

const RankGapTooltip = ({point}) => {
  const datum = byBucket[point.data.x] || {};
  const isSmall = datum.matches < SMALL_SAMPLE;
  return (
    <div className="fifa-tooltip">
      <span className="fifa-tooltip__title">Rank gap {point.data.x}</span>
      <span className="fifa-tooltip__row">
        <span>Win rate</span>
        <span>{datum.winRate}%</span>
      </span>
      <span className="fifa-tooltip__row">
        <span>95% CI</span>
        <span>
          {datum.ciLow}%–{datum.ciHigh}%
        </span>
      </span>
      <span className="fifa-tooltip__row">
        <span>Sample</span>
        <span>n = {datum.matches}</span>
      </span>
      {isSmall ? (
        <span className="fifa-tooltip__caution">
          Small sample — wide CI, interpret with caution.
        </span>
      ) : null}
    </div>
  );
};

export default function RankGapWinRate() {
  const {isDark} = useContext(StyleContext);

  const footnote = (
    <span className="fifa-legend">
      <span className="fifa-legend__item">
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            width: 14,
            height: 10,
            background: FIFA_PALETTE.band,
            border: `1px solid ${FIFA_PALETTE.primary}`,
            marginRight: 6,
            verticalAlign: "middle"
          }}
        />
        95% confidence interval
      </span>
      <span className="fifa-legend__item">
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            width: 14,
            borderTop: `2px dashed ${isDark ? "#9aa4b2" : "#666"}`,
            marginRight: 6,
            verticalAlign: "middle"
          }}
        />
        Coin flip 50%
      </span>
      <span className="fifa-legend__note">
        Buckets are ordered small → large rank gap. The <strong>100-300</strong>{" "}
        bucket has just 2 matches, so its 100% win rate carries a 34%-100% CI —
        not meaningful. Hover any point for its sample size.
      </span>
    </span>
  );

  return (
    <ChartCard
      title="Win rate by rank gap (95% CI)"
      takeaway="Below ~15 ranks apart the favorite is barely better than a coin flip; the signal kicks in only at large gaps."
      footnote={footnote}
    >
      <ResponsiveLine
        data={series}
        margin={{top: 20, right: 30, bottom: 70, left: 60}}
        xScale={{type: "point"}}
        yScale={{type: "linear", min: WIN_RATE_AXIS.min, max: WIN_RATE_AXIS.max}}
        colors={[FIFA_PALETTE.primary]}
        curve="monotoneX"
        lineWidth={3}
        pointSize={9}
        pointColor={{theme: "background"}}
        pointBorderWidth={2}
        pointBorderColor={{from: "serieColor"}}
        enableGridX={false}
        enableArea={false}
        useMesh={true}
        enableSlices={false}
        markers={[coinFlipMarker(isDark)]}
        axisBottom={{
          legend: "Rank gap (positions apart)",
          legendOffset: 54,
          legendPosition: "middle",
          tickRotation: -30
        }}
        axisLeft={{
          legend: WIN_RATE_AXIS.label,
          legendOffset: -46,
          legendPosition: "middle",
          tickValues: [0, 25, 50, 75, 100]
        }}
        layers={[
          "grid",
          "markers",
          "axes",
          "areas",
          "crosshair",
          ConfidenceBand,
          "lines",
          "points",
          "slices",
          "mesh",
          "legends"
        ]}
        tooltip={RankGapTooltip}
        theme={getNivoTheme(isDark)}
        animate={true}
        motionConfig="gentle"
      />
    </ChartCard>
  );
}
